import { useMemo, useState } from 'react';

import type { ProfileCustomization } from '@perplexicon/api-client';

import { Button, Card, ErrorState, StatusMessage } from '../../components/index.js';
import { useCurrentUser, useUpdateProfileCustomization } from '../../lib/auth.js';
import { getErrorMessage } from '../../lib/errors.js';
import { ProfileSectionHeading } from '../profile/components/index.js';
import {
  AVATAR_FRAMES,
  HERO_BACKGROUNDS,
  PROFILE_THEME_PRESETS,
  PROFILE_TITLES,
  SIDE_DECORATIONS,
} from '../profile/profile-assets.js';
import { CosmeticThumbnailPicker } from './profile-appearance/CosmeticThumbnailPicker.js';
import { matchedPresetId } from './profile-appearance/cosmetic-match.js';
import { ProfileAppearancePreview } from './profile-appearance/ProfileAppearancePreview.js';
import { ThemePresetSelector } from './profile-appearance/ThemePresetSelector.js';
import { TitleSelector } from './profile-appearance/TitleSelector.js';

const EMPTY_CUSTOMIZATION: ProfileCustomization = {
  selectedTitleId: null,
  avatarFrameId: null,
  heroBackgroundId: null,
  sideDecorationId: null,
};

type MobileSection = 'avatarFrame' | 'heroBackground' | 'sideDecoration' | null;

/**
 * Profile customization editor (APP_SPEC §13.2), a Settings subsection like
 * the avatar editor. Lets the player pick a title and the three *independent*
 * cosmetic slots, or apply a theme preset that writes all three slot ids at
 * once (after which each remains individually editable — a preset is a
 * convenience, never a lock).
 *
 * Only unlocked options are offered/selectable. The server independently
 * enforces that (`modules/users/profile-cosmetics.ts`); today every
 * in-vocabulary id is unlocked for every user because no achievement system
 * exists yet — a few catalog entries ship `false` purely so this editor's
 * "locked stays locked" behaviour is real and testable.
 *
 * The Live Preview (`ProfileAppearancePreview`) is a purpose-built component
 * for this narrower Settings context — it is not a shrunk `ProfileHero` and
 * carries no stats, matching the public hero's identity material only.
 */
export function ProfileCustomizationEditor() {
  const { data: user } = useCurrentUser();
  const updateCustomization = useUpdateProfileCustomization();

  const saved: ProfileCustomization = user?.customization ?? EMPTY_CUSTOMIZATION;
  const [draft, setDraft] = useState<ProfileCustomization>(saved);

  // Re-seed the draft if the saved value changes underneath us (first load).
  const savedKey = JSON.stringify(saved);
  const [seededFrom, setSeededFrom] = useState(savedKey);
  if (savedKey !== seededFrom && !updateCustomization.isPending) {
    setSeededFrom(savedKey);
    setDraft(saved);
  }

  const isDirty = useMemo(() => JSON.stringify(draft) !== savedKey, [draft, savedKey]);

  const selectedPresetId = useMemo(() => matchedPresetId(draft, PROFILE_THEME_PRESETS), [draft]);

  const [mobileOpenSection, setMobileOpenSection] = useState<MobileSection>(null);
  const toggleMobileSection = (section: MobileSection) =>
    setMobileOpenSection((current) => (current === section ? null : section));

  const set = <K extends keyof ProfileCustomization>(key: K, value: ProfileCustomization[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const applyPreset = (presetId: string) => {
    const preset = PROFILE_THEME_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    setDraft((prev) => ({
      ...prev,
      avatarFrameId: preset.avatarFrameId,
      heroBackgroundId: preset.heroBackgroundId,
      sideDecorationId: preset.sideDecorationId,
    }));
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-textPrimary">Profile Appearance</h2>
            <p className="mt-1 text-sm text-textSecondary">
              Customize how your profile appears to other players.
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="secondary" onClick={() => setDraft(saved)} disabled={!isDirty}>
              Reset
            </Button>
            <Button
              onClick={() => updateCustomization.mutate(draft)}
              isLoading={updateCustomization.isPending}
              disabled={!isDirty}
            >
              Save Changes
            </Button>
          </div>
        </div>
        {updateCustomization.isError && (
          <div className="mt-3">
            <ErrorState message={getErrorMessage(updateCustomization.error)} />
          </div>
        )}
        {updateCustomization.isSuccess && !isDirty && (
          <StatusMessage className="mt-3" tone="success">
            Profile appearance saved.
          </StatusMessage>
        )}
      </Card>

      {user && (
        <section>
          <ProfileSectionHeading icon={null} title="Live Preview" size="sub" className="mb-3" />
          <ProfileAppearancePreview
            userId={user.id}
            username={user.username}
            avatar={user.avatar}
            customization={draft}
          />
        </section>
      )}

      <section>
        <ProfileSectionHeading icon={null} title="Theme Preset" size="sub" className="mb-1" />
        <p className="mb-3 text-sm text-textSecondary">
          A theme preset fills your frame, background, and side decoration. You can still change
          each one afterwards.
        </p>
        <ThemePresetSelector
          presets={PROFILE_THEME_PRESETS}
          selectedId={selectedPresetId}
          onSelect={applyPreset}
        />
      </section>

      <section>
        <ProfileSectionHeading icon={null} title="Customize" size="sub" className="mb-3" />
        <Card className="grid gap-5">
          <TitleSelector
            options={PROFILE_TITLES}
            value={draft.selectedTitleId}
            onChange={(v) => set('selectedTitleId', v)}
          />
          <CosmeticThumbnailPicker
            legend="Avatar Frame"
            options={AVATAR_FRAMES}
            value={draft.avatarFrameId}
            onChange={(v) => set('avatarFrameId', v)}
            aspect="square"
            mobileOpen={mobileOpenSection === 'avatarFrame'}
            onMobileToggle={() => toggleMobileSection('avatarFrame')}
          />
          <CosmeticThumbnailPicker
            legend="Background"
            options={HERO_BACKGROUNDS}
            value={draft.heroBackgroundId}
            onChange={(v) => set('heroBackgroundId', v)}
            aspect="landscape"
            fit="cover"
            mobileOpen={mobileOpenSection === 'heroBackground'}
            onMobileToggle={() => toggleMobileSection('heroBackground')}
          />
          <CosmeticThumbnailPicker
            legend="Side Decoration"
            options={SIDE_DECORATIONS}
            value={draft.sideDecorationId}
            onChange={(v) => set('sideDecorationId', v)}
            aspect="landscape"
            fit="contain"
            mobileOpen={mobileOpenSection === 'sideDecoration'}
            onMobileToggle={() => toggleMobileSection('sideDecoration')}
          />
        </Card>
      </section>
    </div>
  );
}
